import { TemplateFormDialogContents, TemplatesView } from "@tellescope/calendar";
import { CalendarPageTabsHeader } from "./CalendarPageTabsHeader";
import { useCallback, useState } from "react";
import { DestructiveConfirmDialog, Dialog } from "@tellescope/ui";
import { TellescopeModelFromName, useTellescopeMutation, useTellescopeQuery } from "@tellescope/tanstack-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

export const CalendarTemplatesPage = () => {
  const { t } = useTranslation("calendar");
  const [templateBeingDeleted, setTemplateBeingDeleted] =
    useState<TellescopeModelFromName<"calendar_event_templates">>();
  const [search, setSearch] = useSearchParams();
  const templateIdBeingDuplicated = search.get("duplicateTemplateId") ?? undefined;
  const templateIdBeingEdited = search.get("templateId") ?? templateIdBeingDuplicated ?? undefined;
  const [isCreating, setIsCreating] = useState(false);

  const deleteTemplateMutation = useTellescopeMutation({
    model: "calendar_event_templates",
    method: "deleteOne",
    onSuccess: () => setTemplateBeingDeleted(undefined),
    onError: () => toast.error(t("Error deleting template, please try again")),
  });

  const templateQuery = useTellescopeQuery({
    model: "calendar_event_templates",
    method: "getOne",
    request: templateIdBeingEdited ?? "",
    enabled: !!templateIdBeingEdited,
  });

  const closeDialog = useCallback(() => {
    setSearch(
      (prev) => {
        prev.delete("templateId");
        prev.delete("duplicateTemplateId");
        return prev;
      },
      { replace: true }
    );
    setIsCreating(false);
  }, [setSearch]);

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      <CalendarPageTabsHeader />
      <TemplatesView
        getTemplateOnClick={(template) => ({
          to: `.?templateId=${template.id}`,
          replace: true,
        })}
        onDelete={(template) => setTemplateBeingDeleted(template)}
        onAddTemplateClick={() => {
          setIsCreating(true);
        }}
        getTemplateOnDuplicate={(template) => ({
          to: `.?duplicateTemplateId=${template.id}`,
          replace: true,
        })}
      />
      <DestructiveConfirmDialog
        open={!!templateBeingDeleted}
        onOpenChange={(open) => {
          if (!open) {
            setTemplateBeingDeleted(undefined);
          }
        }}
        onConfirm={() => {
          if (templateBeingDeleted?.id) deleteTemplateMutation.mutateAsync(templateBeingDeleted?.id);
        }}
        onCancel={() => setTemplateBeingDeleted(undefined)}
        loading={deleteTemplateMutation.isPending}
        title={t("Delete template?")}
        confirmButtonTestId="template-delete-confirm"
        description={
          templateBeingDeleted?.title
            ? t('"{{templateName}}" will be permanently removed. This cannot be undone.', {
                templateName: templateBeingDeleted?.displayTitle || templateBeingDeleted.title,
              })
            : t("This template will be permanently removed. This cannot be undone.")
        }
      />
      <Dialog
        open={templateIdBeingEdited ? !!templateQuery.data : isCreating}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog();
          }
        }}
      >
        <TemplateFormDialogContents
          key={`template-${
            templateIdBeingDuplicated === templateIdBeingEdited
              ? "duplication"
              : isCreating
              ? "creation"
              : templateIdBeingEdited
              ? "editing"
              : "closed"
          }-dialog`}
          template={templateQuery.data}
          onCancel={() => {
            closeDialog();
          }}
          onSave={() => {
            closeDialog();
          }}
          shouldDuplicate={templateIdBeingEdited === templateIdBeingDuplicated}
        />
      </Dialog>
    </div>
  );
};
